import { useEffect, useState, useCallback } from "react";
import { Star, TrendingUp, MessageCircle, Loader2, Filter, RefreshCw } from "lucide-react";
import api from "../services/api";
import { Button } from "../components/ui/button";

/**
 * Sprint 74 — Admin feedback dashboard.
 *
 * Surfaces all customer NPS submissions to admin / founder / ceo.
 * Top summary tile shows NPS score (-100..+100), promoter / passive /
 * detractor counts, and average raw score. Filterable by category;
 * paginated via skip/limit. Read-only — feedback is never edited by
 * admins, but each row links back to the order tracking page so the
 * admin can pull the full trace.
 */
const PAGE_SIZE = 25;

export default function AdminFeedback() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [category, setCategory] = useState("all");
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip };
      if (category !== "all") params.category = category;
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/admin/feedback", { params }),
        api.get("/admin/feedback/summary"),
      ]);
      setRows(list.feedback || []);
      setTotal(list.total || 0);
      setSummary(sum);
    } finally {
      setLoading(false);
    }
  }, [category, skip]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-feedback-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Customer Feedback</h1>
          <p className="text-sm text-slate-500">NPS responses from delivered orders</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="feedback-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="feedback-summary">
          <SummaryTile label="NPS Score" value={summary.nps_score ?? "—"} icon={TrendingUp} color="text-orange-600" />
          <SummaryTile label="Avg Score" value={summary.avg_score ?? "—"} icon={Star} color="text-amber-500" />
          <SummaryTile label="Promoters" value={summary.promoters} sub={`/${summary.total}`} color="text-emerald-600" />
          <SummaryTile label="Detractors" value={summary.detractors} sub={`/${summary.total}`} color="text-rose-600" />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-slate-400" />
          <FilterChip active={category === "all"} onClick={() => { setSkip(0); setCategory("all"); }} testId="filter-all">All</FilterChip>
          <FilterChip active={category === "promoter"} onClick={() => { setSkip(0); setCategory("promoter"); }} testId="filter-promoter">Promoters</FilterChip>
          <FilterChip active={category === "passive"} onClick={() => { setSkip(0); setCategory("passive"); }} testId="filter-passive">Passives</FilterChip>
          <FilterChip active={category === "detractor"} onClick={() => { setSkip(0); setCategory("detractor"); }} testId="filter-detractor">Detractors</FilterChip>
          <div className="flex-1" />
          <span className="text-xs text-slate-400">{total} total</span>
        </div>

        {loading && (
          <div className="p-8 text-center text-slate-400">
            <Loader2 className="h-5 w-5 mx-auto animate-spin" />
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="p-8 text-center text-slate-400">
            <MessageCircle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            No feedback yet for this filter.
          </div>
        )}

        {!loading && rows.length > 0 && (
          <ul className="divide-y divide-slate-100" data-testid="feedback-list">
            {rows.map((r) => (
              <li key={r.id} className="px-4 py-3.5 hover:bg-slate-50/50" data-testid={`feedback-row-${r.tracking_id}`}>
                <div className="flex items-start gap-3">
                  <ScorePill score={r.nps_score} category={r.nps_category} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-900">{r.user_name || "Anonymous"}</span>
                      <a
                        href={`/track/${r.tracking_id}`}
                        target="_blank" rel="noopener noreferrer"
                        className="font-mono text-xs text-orange-600 hover:underline"
                      >
                        {r.tracking_id}
                      </a>
                      <span className="text-[10px] text-slate-400 ml-auto">{formatTs(r.created_at)}</span>
                    </div>
                    {r.comment && (
                      <p className="text-sm text-slate-600 mt-1 italic">"{r.comment}"</p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {!loading && total > PAGE_SIZE && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <span>
              Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} data-testid="feedback-prev">Prev</Button>
              <Button size="sm" variant="outline" disabled={skip + PAGE_SIZE >= total} onClick={() => setSkip(skip + PAGE_SIZE)} data-testid="feedback-next">Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryTile({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{label}</span>
        {Icon && <Icon className={`h-4 w-4 ${color}`} />}
      </div>
      <div className="mt-1.5 flex items-baseline gap-1">
        <span className={`text-2xl font-bold ${color}`}>{value}</span>
        {sub && <span className="text-xs text-slate-400">{sub}</span>}
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border min-h-[32px] ${
        active
          ? "bg-orange-500 text-white border-orange-500"
          : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function ScorePill({ score, category }) {
  const colors = {
    promoter: "bg-emerald-100 text-emerald-700",
    passive: "bg-amber-100 text-amber-700",
    detractor: "bg-rose-100 text-rose-700",
  };
  return (
    <div className={`shrink-0 w-10 h-10 rounded-xl ${colors[category] || "bg-slate-100 text-slate-700"} flex items-center justify-center font-bold`}>
      {score}
    </div>
  );
}

function formatTs(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ts; }
}
